/**
 * `<InheritedLayouts>` — the Layout panel's rows for a **virtual group** being
 * authored (`docs/plans/group-sharing.md`, mock 2):
 *
 *     From JAS
 *       JAS Layout          v5   🔗 inherited
 *           Screener   Impulse  →  [ Combo        ▾ ]      [Save]
 *       Market Breadth      v1   🔗 inherited
 *           Screener   (none)
 *
 * **Read-only**, because their content belongs to JAS and JP and is edited as
 * them; the one thing JAS-JP owns here is the override, one row per screen a
 * layout's screener shows. The dropdown offers JAS-JP's own screens and
 * *— keep —*, which deletes the override.
 *
 * Which screen a layout shows is not in the listing, so each layout's
 * definition is read once when the panel opens. Everything goes through the
 * injected loaders and the one save, so this component knows no URL.
 */

import { useEffect, useState } from "react";
import type { Layout } from "@qufolio/model";

import type { LayoutOverride, LayoutSummary } from "../api/layouts";
import { screenerConfig } from "../lib/section-config";

export interface InheritedLayoutsProps {
  /** The groups the virtual group includes, in order — `[{key: "grp-jas", name: "JAS"}]`. */
  includes: readonly { key: string; name: string }[];
  layoutPresets: readonly LayoutSummary[];
  /** The virtual group's own screens: what a screener may be switched to. */
  ownScreens: readonly { key: string; name: string }[];
  /** A screen's name for the `Impulse →` half; the key when it is not known. */
  screenName: (key: string) => string;
  loadLayout: (key: string) => Promise<Partial<Layout> | null>;
  loadOverrides: () => Promise<LayoutOverride[]>;
  /** `to: null` removes the override. Throws on refusal. */
  saveOverride: (layoutKey: string, from: string, to: string | null) => Promise<void>;
}

/** `layoutKey|from` — one override's identity. */
const idOf = (layoutKey: string, from: string) => `${layoutKey}|${from}`;

export function InheritedLayouts({
  includes,
  layoutPresets,
  ownScreens,
  screenName,
  loadLayout,
  loadOverrides,
  saveOverride,
}: InheritedLayoutsProps) {
  const inherited = layoutPresets.filter(
    (p) => p.ownerKind === "org" && includes.some((g) => g.key === p.ownerKey),
  );
  const keys = inherited.map((p) => p.key).join("|");

  /** Per layout: the screens its screeners show, or `undefined` while reading. */
  const [showing, setShowing] = useState<ReadonlyMap<string, string[] | null>>(new Map());
  /** The overrides as saved, by `layoutKey|from`. */
  const [saved, setSaved] = useState<ReadonlyMap<string, string>>(new Map());
  /** The dropdowns as chosen, by `layoutKey|from`; `""` is *keep*. */
  const [chosen, setChosen] = useState<ReadonlyMap<string, string>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void loadOverrides().then((rows) => {
      if (alive) setSaved(new Map(rows.map((r) => [idOf(r.layoutKey, r.from), r.to])));
    });
    return () => {
      alive = false;
    };
  }, [loadOverrides]);

  useEffect(() => {
    let alive = true;
    for (const key of keys ? keys.split("|") : []) {
      void loadLayout(key).then((definition) => {
        if (!alive) return;
        const refs = definition
          ? [
              ...new Set(
                (definition.sections ?? []).flatMap((s) => {
                  const ref = screenerConfig(s)?.screenRef;
                  return ref ? [ref] : [];
                }),
              ),
            ]
          : null;
        setShowing((had) => new Map(had).set(key, refs));
      });
    }
    return () => {
      alive = false;
    };
  }, [keys, loadLayout]);

  const save = async (layoutKey: string, from: string) => {
    const id = idOf(layoutKey, from);
    const to = chosen.get(id) ?? saved.get(id) ?? "";
    setBusy(id);
    setError(null);
    try {
      await saveOverride(layoutKey, from, to || null);
      setSaved((had) => {
        const next = new Map(had);
        if (to) next.set(id, to);
        else next.delete(id);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  if (inherited.length === 0) return null;

  return (
    <>
      {includes.map((group) => {
        const rows = inherited.filter((p) => p.ownerKey === group.key);
        if (rows.length === 0) return null;
        return (
          <div key={group.key} style={GROUP}>
            <div style={SECTION_LABEL}>From {group.name}</div>
            {rows.map((preset) => {
              const refs = showing.get(preset.key);
              return (
                <div key={preset.key} style={{ padding: "2px 0" }}>
                  <div style={ROW} title={`Edited as ${group.name} — read-only here`}>
                    <span style={NAME}>{preset.name}</span>
                    <span style={MUTED}>{preset.version === undefined ? "—" : `v${preset.version}`}</span>
                    <span style={MUTED}>🔗 inherited</span>
                  </div>
                  {refs === undefined && <div style={SCREENER_ROW}>Screener …</div>}
                  {refs !== undefined && (refs === null || refs.length === 0) && (
                    <div style={SCREENER_ROW}>
                      <span style={MUTED}>Screener</span> <span style={MUTED}>(none)</span>
                    </div>
                  )}
                  {refs?.map((from) => {
                    const id = idOf(preset.key, from);
                    const held = saved.get(id) ?? "";
                    const value = chosen.get(id) ?? held;
                    return (
                      <div key={from} style={SCREENER_ROW}>
                        <span style={MUTED}>Screener</span>
                        <span>{screenName(from)}</span>
                        <span aria-hidden>→</span>
                        <select
                          aria-label={`${preset.name}: show instead of ${screenName(from)}`}
                          value={value}
                          onChange={(e) => setChosen((had) => new Map(had).set(id, e.target.value))}
                          style={SELECT}
                        >
                          <option value="">— keep —</option>
                          {ownScreens.map((s) => (
                            <option key={s.key} value={s.key}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                        {value !== held && (
                          <button
                            type="button"
                            disabled={busy === id}
                            onClick={() => void save(preset.key, from)}
                            style={BUTTON}
                          >
                            {busy === id ? "Saving…" : "Save"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
      {error && <div style={ERROR}>{error}</div>}
    </>
  );
}

const GROUP: React.CSSProperties = {
  padding: 8,
  borderTop: "1px solid var(--qf-border)",
};

/** `LayoutPanel`'s, so these read as its own groups. */
const SECTION_LABEL: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
  padding: "2px 4px 6px",
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: "0.8rem",
};

const NAME: React.CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "var(--qf-text)",
};

const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)", fontVariantNumeric: "tabular-nums" };

const SCREENER_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  paddingLeft: 16,
  fontSize: "0.76rem",
  color: "var(--qf-text-secondary)",
};

const SELECT: React.CSSProperties = {
  font: "inherit",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
};

const BUTTON: React.CSSProperties = {
  font: "inherit",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0 8px",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  cursor: "pointer",
};

const ERROR: React.CSSProperties = {
  padding: "0 8px 8px",
  fontSize: "0.74rem",
  color: "var(--qf-negative)",
};
